// frontend/src/pages/TVDashboard.jsx

import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  Box,
  Typography,
  Paper,
  Grid,
  useTheme,
  alpha,
  CircularProgress,
} from '@mui/material';
import {
  ShoppingCart as OrdersIcon,
  Inventory as ProductionIcon,
  People as CustomersIcon,
  LocalShipping as ShippingIcon,
  Schedule as ScheduleIcon,
} from '@mui/icons-material';
import {
  LineChart,
  Line,
  BarChart,
  Bar,
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  ReferenceLine,
} from 'recharts';
import { tvDashboardAPI } from '../services/api';
import usePageTitle from '../hooks/usePageTitle';


// Animated number component
const AnimatedNumber = ({ value, duration = 1000, prefix = '', suffix = '', decimals = 0 }) => {
  const [displayValue, setDisplayValue] = useState(0);
  const previousValue = useRef(0);
  const animationRef = useRef(null);

  useEffect(() => {
    const startValue = previousValue.current;
    const endValue = typeof value === 'number' ? value : 0;
    const startTime = performance.now();

    const animate = (currentTime) => {
      const elapsed = currentTime - startTime;
      const progress = Math.min(elapsed / duration, 1);
      
      const easeOutQuart = 1 - Math.pow(1 - progress, 4);
      
      const currentValue = startValue + (endValue - startValue) * easeOutQuart;
      setDisplayValue(currentValue);

      if (progress < 1) {
        animationRef.current = requestAnimationFrame(animate);
      } else {
        previousValue.current = endValue;
      }
    };

    animationRef.current = requestAnimationFrame(animate);

    return () => {
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }
    };
  }, [value, duration]);

  const formattedValue = decimals > 0 
    ? displayValue.toFixed(decimals) 
    : Math.round(displayValue).toLocaleString();

  return (
    <span>
      {prefix}{formattedValue}{suffix}
    </span>
  );
};


// Stat card component
const StatCard = ({ title, value, subtitle, icon: Icon, color, prefix = '', suffix = '' }) => {
  const theme = useTheme();
  
  return (
    <Paper
      elevation={0}
      sx={{
        p: 3,
        height: '100%',
        background: `linear-gradient(135deg, ${alpha(color, 0.1)} 0%, ${alpha(color, 0.05)} 100%)`,
        border: `1px solid ${alpha(color, 0.2)}`,
        borderRadius: 3,
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'space-between',
      }}
    >
      <Box className="page-header-row" sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
        <Typography 
          variant="body1" 
          sx={{ 
            color: theme.palette.text.secondary,
            fontWeight: 500,
            textTransform: 'uppercase',
            letterSpacing: 0.5,
            fontSize: '0.85rem',
          }}
        >
          {title}
        </Typography>
        <Box
          sx={{
            p: 1,
            borderRadius: 2,
            backgroundColor: alpha(color, 0.15),
          }}
        >
          <Icon sx={{ color: color, fontSize: 28 }} />
        </Box>
      </Box>
      
      <Typography 
        variant="h3" 
        sx={{ 
          fontWeight: 700,
          color: theme.palette.text.primary,
          lineHeight: 1,
          mb: 1,
        }}
      >
        <AnimatedNumber value={value} prefix={prefix} suffix={suffix} />
      </Typography>
      
      {subtitle && (
        <Typography 
          variant="body2" 
          sx={{ 
            color: theme.palette.text.secondary,
            mt: 1,
          }}
        >
          {subtitle}
        </Typography>
      )}
    </Paper>
  );
};


// Mini stat for sub-values
const MiniStat = ({ label, value, color }) => {
  const theme = useTheme();
  
  return (
    <Box sx={{ textAlign: 'center', px: 2 }}>
      <Typography 
        variant="h5" 
        sx={{ 
          fontWeight: 700, 
          color: color || theme.palette.text.primary,
          lineHeight: 1,
        }}
      >
        <AnimatedNumber value={value} />
      </Typography>
      <Typography 
        variant="caption" 
        sx={{ 
          color: theme.palette.text.secondary,
          textTransform: 'uppercase',
          letterSpacing: 0.5,
        }}
      >
        {label}
      </Typography>
    </Box>
  );
};


// Custom tooltip for intraday chart
const IntradayTooltip = ({ active, payload, label, target }) => {
  const theme = useTheme();
  
  if (active && payload && payload.length && payload[0]?.value !== null) {
    const completed = payload[0]?.value || 0;
    const remaining = (target || 0) - completed;
    const percentage = target > 0 ? Math.round((completed / target) * 100) : 0;
    
    return (
      <Box
        sx={{
          backgroundColor: theme.palette.background.paper,
          border: `1px solid ${theme.palette.divider}`,
          borderRadius: 2,
          p: 1.5,
          boxShadow: theme.shadows[4],
        }}
      >
        <Typography variant="body2" fontWeight={600}>
          {label}
        </Typography>
        <Typography variant="body2" color="success.main">
          Completed: {completed} / {target}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Remaining: {remaining > 0 ? remaining : 0}
        </Typography>
        <Typography variant="body2" color="primary.main" fontWeight={600}>
          Progress: {percentage}%
        </Typography>
        <Typography 
          variant="body2" 
          color="text.secondary" 
          sx={{ 
            mt: 0.5, 
            borderTop: `1px solid ${theme.palette.divider}`, 
            pt: 0.5 
          }}
        >
          Units: {payload[0]?.payload?.cumulative_units?.toLocaleString()}
        </Typography>
      </Box>
    );
  }
  return null;
};


const TVDashboard = () => {
  usePageTitle('TV Dashboard');
  const theme = useTheme();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [lastUpdated, setLastUpdated] = useState(null);

  const fetchStats = useCallback(async () => {
    try {
      const response = await tvDashboardAPI.getStats();
      setStats(response.data);
      setLastUpdated(new Date());
    } catch (error) {
      console.error('Failed to fetch TV dashboard stats:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchStats();
    
    // Auto-refresh every 30 seconds
    const interval = setInterval(fetchStats, 30000);
    
    return () => clearInterval(interval);
  }, [fetchStats]);

  // Colors for charts and cards
  const colors = {
    orders: theme.palette.primary.main,
    production: theme.palette.success.main,
    customers: theme.palette.info.main,
    shipping: theme.palette.warning.main,
    inventory: theme.palette.secondary.main,
  };

  const pieColors = [
    theme.palette.warning.main,
    theme.palette.info.main,
    theme.palette.primary.main,
    theme.palette.success.main,
    theme.palette.success.light,
    theme.palette.grey[400],
  ];

  if (loading) {
    return (
      <Box 
        sx={{ 
          display: 'flex', 
          justifyContent: 'center', 
          alignItems: 'center', 
          minHeight: '100vh',
          backgroundColor: theme.palette.background.default,
        }}
      >
        <CircularProgress size={60} />
      </Box>
    );
  }

  if (!stats) {
    return (
      <Box sx={{ p: 4, textAlign: 'center' }}>
        <Typography variant="h5" color="error">
          Failed to load dashboard data
        </Typography>
      </Box>
    );
  }

  // Prepare pie chart data
  const orderStatusData = [
    { name: 'New', value: stats.orders.by_status.new },
    { name: 'Confirmed', value: stats.orders.by_status.confirmed },
    { name: 'In Production', value: stats.orders.by_status.in_production },
    { name: 'Ready to Ship', value: stats.orders.by_status.ready_to_ship },
    { name: 'Partially Shipped', value: stats.orders.by_status.partially_shipped },
  ].filter(item => item.value > 0);

  // Format chart data
  const ordersChartData = stats.orders.by_day.map(item => ({
    date: new Date(item.date).toLocaleDateString('en-GB', { day: '2-digit', month: 'short' }),
    orders: item.count,
  }));

  const productionChartData = stats.production.by_day.map(item => ({
    date: new Date(item.date).toLocaleDateString('en-GB', { day: '2-digit', month: 'short' }),
    units: item.units,
  }));

  // Intraday chart data
  const intradayData = stats.production.intraday || [];
  const currentTime = stats.current_time || '';
  const dailyTarget = stats.production.daily_target || 0;

  return (
    <Box
      sx={{
        minHeight: '100vh',
        backgroundColor: theme.palette.background.default,
        p: 3,
      }}
    >
      {/* Header */}
      <Box className="page-header-row" sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography 
          variant="h4" 
          sx={{ 
            fontWeight: 700,
            color: theme.palette.text.primary,
          }}
        >
          Production Dashboard
        </Typography>
        
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <ScheduleIcon sx={{ color: theme.palette.text.secondary, fontSize: 20 }} />
          <Typography variant="body2" sx={{ color: theme.palette.text.secondary }}>
            Last updated: {lastUpdated?.toLocaleTimeString()}
          </Typography>
        </Box>
      </Box>

      {/* Main Stats Row */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Active Orders"
            value={stats.orders.total_active}
            subtitle={`${stats.orders.this_week} this week`}
            icon={OrdersIcon}
            color={colors.orders}
          />
        </Grid>
        
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Units Produced"
            value={stats.production.units_produced.this_month}
            subtitle={`${Math.round(stats.production.units_produced.today)} today`}
            icon={ProductionIcon}
            color={colors.production}
          />
        </Grid>
        
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Total Customers"
            value={stats.customers.total}
            subtitle={`${stats.customers.new_this_month} new this month`}
            icon={CustomersIcon}
            color={colors.customers}
          />
        </Grid>
        
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Units Shipped"
            value={stats.shipping.units_shipped.this_month}
            subtitle={`${Math.round(stats.shipping.units_shipped.today)} today`}
            icon={ShippingIcon}
            color={colors.shipping}
          />
        </Grid>
      </Grid>

      {/* Intraday Production Chart - Full Width */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12}>
          <Paper
            elevation={0}
            sx={{
              p: 3,
              height: 350,
              border: `1px solid ${theme.palette.divider}`,
              borderRadius: 3,
            }}
          >
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Typography variant="h6" sx={{ fontWeight: 600 }}>
                Today's Production (7am - 5pm)
              </Typography>
              <Box sx={{ display: 'flex', gap: 3, alignItems: 'center' }}>
                <Box sx={{ textAlign: 'right' }}>
                  <Typography variant="h4" sx={{ fontWeight: 700, color: colors.production, lineHeight: 1 }}>
                    <AnimatedNumber value={stats.production.work_orders_completed.today} />
                    <Typography 
                      component="span" 
                      variant="h6" 
                      sx={{ color: theme.palette.text.secondary, fontWeight: 400 }}
                    >
                      {' / '}{dailyTarget}
                    </Typography>
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Work Orders Completed
                  </Typography>
                </Box>
                <Box sx={{ textAlign: 'right' }}>
                  <Typography variant="h4" sx={{ fontWeight: 700, color: colors.production, lineHeight: 1 }}>
                    <AnimatedNumber value={stats.production.units_produced.today} />
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Units Produced
                  </Typography>
                </Box>
              </Box>
            </Box>
            
            <ResponsiveContainer width="100%" height="85%">
              <AreaChart data={intradayData}>
                <defs>
                  <linearGradient id="colorProduction" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor={colors.production} stopOpacity={0.4}/>
                    <stop offset="95%" stopColor={colors.production} stopOpacity={0.1}/>
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke={theme.palette.divider} />
                <XAxis 
                  dataKey="time" 
                  tick={{ fontSize: 11 }}
                  stroke={theme.palette.text.secondary}
                  interval={11}
                  tickLine={{ stroke: theme.palette.divider }}
                />
                <YAxis 
                  tick={{ fontSize: 11 }}
                  stroke={theme.palette.text.secondary}
                  allowDecimals={false}
                  domain={[0, Math.max(dailyTarget || 10, stats.production.work_orders_completed.today || 0) + 5]}
                  tickLine={{ stroke: theme.palette.divider }}
                />
                <Tooltip content={<IntradayTooltip target={dailyTarget} />} />
                
                {/* Target line - outstanding work orders */}
                <ReferenceLine 
                  y={dailyTarget} 
                  stroke={theme.palette.primary.main}
                  strokeWidth={2}
                  strokeDasharray="8 4"
                  label={{ 
                    value: `Target: ${dailyTarget}`, 
                    position: 'right', 
                    fill: theme.palette.primary.main,
                    fontSize: 12,
                    fontWeight: 600
                  }}
                />
                
                {/* Current time marker */}
                {currentTime && (
                  <ReferenceLine 
                    x={currentTime} 
                    stroke={theme.palette.error.main} 
                    strokeWidth={2}
                    strokeDasharray="5 5"
                    label={{ 
                      value: 'Now', 
                      position: 'top', 
                      fill: theme.palette.error.main,
                      fontSize: 12,
                      fontWeight: 600
                    }}
                  />
                )}
                
                <Area
                  type="stepAfter"
                  dataKey="cumulative_count"
                  stroke={colors.production}
                  strokeWidth={2}
                  fill="url(#colorProduction)"
                  dot={false}
                  activeDot={{ r: 4, fill: colors.production }}
                  connectNulls={false}
                />
              </AreaChart>
            </ResponsiveContainer>
          </Paper>
        </Grid>
      </Grid>

      {/* Charts Row */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        {/* Orders Chart */}
        <Grid item xs={12} md={8}>
          <Paper
            elevation={0}
            sx={{
              p: 3,
              height: 300,
              border: `1px solid ${theme.palette.divider}`,
              borderRadius: 3,
            }}
          >
            <Typography 
              variant="h6" 
              sx={{ mb: 2, fontWeight: 600 }}
            >
              Orders (Last 30 Days)
            </Typography>
            
            <ResponsiveContainer width="100%" height="85%">
              <LineChart data={ordersChartData}>
                <CartesianGrid strokeDasharray="3 3" stroke={theme.palette.divider} />
                <XAxis 
                  dataKey="date" 
                  tick={{ fontSize: 11 }}
                  stroke={theme.palette.text.secondary}
                />
                <YAxis 
                  tick={{ fontSize: 11 }}
                  stroke={theme.palette.text.secondary}
                />
                <Tooltip 
                  contentStyle={{
                    backgroundColor: theme.palette.background.paper,
                    border: `1px solid ${theme.palette.divider}`,
                    borderRadius: 8,
                  }}
                />
                <Line
                  type="monotone"
                  dataKey="orders"
                  stroke={colors.orders}
                  strokeWidth={3}
                  dot={{ fill: colors.orders, strokeWidth: 2, r: 4 }}
                  activeDot={{ r: 6 }}
                />
              </LineChart>
            </ResponsiveContainer>
          </Paper>
        </Grid>

        {/* Order Status Pie Chart */}
        <Grid item xs={12} md={4}>
          <Paper
            elevation={0}
            sx={{
              p: 3,
              height: 300,
              border: `1px solid ${theme.palette.divider}`,
              borderRadius: 3,
            }}
          >
            <Typography 
              variant="h6" 
              sx={{ mb: 2, fontWeight: 600 }}
            >
              Order Status
            </Typography>
            
            <ResponsiveContainer width="100%" height="85%">
              <PieChart>
                <Pie
                  data={orderStatusData}
                  cx="50%"
                  cy="50%"
                  innerRadius={50}
                  outerRadius={80}
                  paddingAngle={3}
                  dataKey="value"
                  label={({ name, value }) => `${name}: ${value}`}
                  labelLine={false}
                >
                  {orderStatusData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={pieColors[index % pieColors.length]} />
                  ))}
                </Pie>
                <Tooltip 
                  contentStyle={{
                    backgroundColor: theme.palette.background.paper,
                    border: `1px solid ${theme.palette.divider}`,
                    borderRadius: 8,
                  }}
                />
              </PieChart>
            </ResponsiveContainer>
          </Paper>
        </Grid>
      </Grid>

      {/* Bottom Row */}
      <Grid container spacing={3}>
        {/* Production Chart */}
        <Grid item xs={12} md={8}>
          <Paper
            elevation={0}
            sx={{
              p: 3,
              height: 300,
              border: `1px solid ${theme.palette.divider}`,
              borderRadius: 3,
            }}
          >
            <Typography 
              variant="h6" 
              sx={{ mb: 2, fontWeight: 600 }}
            >
              Production Output (Last 30 Days)
            </Typography>
            
            <ResponsiveContainer width="100%" height="85%">
              <BarChart data={productionChartData}>
                <CartesianGrid strokeDasharray="3 3" stroke={theme.palette.divider} />
                <XAxis 
                  dataKey="date" 
                  tick={{ fontSize: 11 }}
                  stroke={theme.palette.text.secondary}
                />
                <YAxis 
                  tick={{ fontSize: 11 }}
                  stroke={theme.palette.text.secondary}
                />
                <Tooltip 
                  contentStyle={{
                    backgroundColor: theme.palette.background.paper,
                    border: `1px solid ${theme.palette.divider}`,
                    borderRadius: 8,
                  }}
                />
                <Bar 
                  dataKey="units" 
                  fill={colors.production}
                  radius={[4, 4, 0, 0]}
                />
              </BarChart>
            </ResponsiveContainer>
          </Paper>
        </Grid>

        {/* Work Order Summary */}
        <Grid item xs={12} md={4}>
          <Paper
            elevation={0}
            sx={{
              p: 3,
              height: 300,
              border: `1px solid ${theme.palette.divider}`,
              borderRadius: 3,
              display: 'flex',
              flexDirection: 'column',
            }}
          >
            <Typography 
              variant="h6" 
              sx={{ mb: 3, fontWeight: 600 }}
            >
              Work Orders
            </Typography>
            
            <Box sx={{ display: 'flex', justifyContent: 'space-around', mb: 4 }}>
              <MiniStat 
                label="Active" 
                value={stats.production.active_work_orders.total}
                color={theme.palette.primary.main}
              />
              <MiniStat 
                label="In Progress" 
                value={stats.production.active_work_orders.in_progress}
                color={theme.palette.warning.main}
              />
            </Box>
            
            <Box 
              sx={{ 
                mt: 'auto',
                pt: 3,
                borderTop: `1px solid ${theme.palette.divider}`,
                display: 'flex',
                justifyContent: 'space-around',
              }}
            >
              <Box sx={{ textAlign: 'center' }}>
                <Typography variant="h4" sx={{ fontWeight: 700, color: theme.palette.success.main }}>
                  <AnimatedNumber value={stats.production.work_orders_completed.today} />
                </Typography>
                <Typography 
                  variant="body2" 
                  sx={{ 
                    color: theme.palette.text.secondary,
                    textTransform: 'uppercase',
                    letterSpacing: 0.5,
                  }}
                >
                  Completed Today
                </Typography>
              </Box>
              
              <Box sx={{ textAlign: 'center' }}>
                <Typography variant="h4" sx={{ fontWeight: 700, color: theme.palette.success.main }}>
                  <AnimatedNumber value={stats.production.work_orders_completed.this_month} />
                </Typography>
                <Typography 
                  variant="body2" 
                  sx={{ 
                    color: theme.palette.text.secondary,
                    textTransform: 'uppercase',
                    letterSpacing: 0.5,
                  }}
                >
                  This Month
                </Typography>
              </Box>
            </Box>
          </Paper>
        </Grid>
      </Grid>
    </Box>
  );
};

export default TVDashboard;